// ============================================================================
// BOQ — SD area-rate picker (estimate BOQ)
// ============================================================================
// Opened from a line's "Rates" button when that line is an SD area-rate
// allowance (allowance line type + a building-area measure basis). Resolves the
// line's subtrade to its brio_trade catalogue node, then shows the
// cost-index-escalated cross-project median (with quartile spread + the
// contributing projects) and lets the estimator apply the median — or any
// individual escalated rate — into the line's rate cell.
//
// For non-area lines, ProjectItemsSection opens RateLibraryModal (item rates)
// instead; this modal is the SD/subtrade-granularity counterpart.
// ============================================================================

import React, { useMemo, useState } from 'react';
import {
  BrioModal, BrioModalBody, BrioButton, BrioSelect, BrioKpiBlock,
} from '../../components/BrioComponents';
import { useBoqSdRates } from '../hooks/useBoqSdRates';
import { useBoqCostIndex } from '../hooks/useBoqCostIndex';
import { useBoqTaxonomies } from '../hooks/useBoqTaxonomies';
import {
  summarizeRates, measureBasisLabel, MEASURE_BASIS_LABELS, spreadDisplay,
} from '../utils/sdRateStats';

const today = () => new Date().toISOString().slice(0, 10);
const fmt = (v) => (v == null ? '—' : `₹${Math.round(Number(v)).toLocaleString('en-IN')}`);
const round2 = (v) => Number(Number(v).toFixed(2));

const cell = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid var(--brio-border-subtle, #eee)' };
const th = { ...cell, textAlign: 'left', fontWeight: 600, color: 'var(--brio-text-muted)', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.03em', whiteSpace: 'nowrap' };
const numCell = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

function SdRatePickerModal({ line, currency = 'INR', billView = 'quoted', onClose, onApplyRate }) {
  const sdApi = useBoqSdRates();
  const costIndex = useBoqCostIndex();
  const { taxonomies, loading: taxLoading } = useBoqTaxonomies();
  const asOf = today();
  const target = billView === 'billed' ? 'billed' : 'quoted';

  // Resolve this line's (trade, subtrade) to its brio_trade catalogue node id.
  const nodeId = useMemo(() => {
    const brio = (taxonomies || []).find((t) => t.code === 'brio_trade');
    if (!brio) return null;
    const byId = new Map(brio.nodes.map((n) => [n.id, n]));
    const sub = (line.subtrade || '').toLowerCase();
    const trade = (line.trade || '').toLowerCase();
    const match = brio.nodes.find((n) => n.parentId
      && n.label.toLowerCase() === sub
      && (byId.get(n.parentId)?.label || '').toLowerCase() === trade);
    return match?.id || null;
  }, [taxonomies, line]);

  // Default to the line's own basis; allow widening to all bases.
  const [basisMode, setBasisMode] = useState(line.measureBasis || 'all');

  const rates = useMemo(
    () => sdApi.rates.filter((r) => r.subtradeNodeId === nodeId
      && (basisMode === 'all' || r.measureBasis === basisMode)),
    [sdApi.rates, nodeId, basisMode],
  );

  const stats = useMemo(
    () => summarizeRates(rates, { adjust: costIndex.adjust, factor: costIndex.factor, asOfDate: asOf }),
    [rates, costIndex.adjust, costIndex.factor, asOf],
  );
  const spread = spreadDisplay(stats);

  const apply = (value) => {
    if (value == null) return;
    onApplyRate(round2(value), target);
  };

  const loading = sdApi.loading || taxLoading || costIndex.loading;

  const footer = (
    <>
      <BrioButton variant="secondary" onClick={onClose}>Close</BrioButton>
      <BrioButton variant="primary" onClick={() => apply(stats.median)} disabled={stats.median == null}>
        Apply median ({fmt(stats.median)})
      </BrioButton>
    </>
  );

  return (
    <BrioModal title="SD area-rate suggestion" onClose={onClose} width="md" footer={footer}>
      <BrioModalBody>
        <div style={{ marginBottom: '0.75rem', fontSize: '0.9rem' }}>
          <strong>{line.subtrade || '(no subtrade)'}</strong>
          {line.trade ? <span style={{ color: 'var(--brio-text-muted)' }}> · {line.trade}</span> : null}
        </div>

        {loading ? (
          <div style={{ padding: '1rem', color: 'var(--brio-text-muted)' }}>Loading rates…</div>
        ) : !nodeId ? (
          <div style={{ padding: '0.85rem 1rem', background: '#fff7e6', borderRadius: 6, fontSize: '0.85rem' }}>
            Couldn’t match this line’s subtrade “{line.subtrade}” (trade “{line.trade}”) to the brio_trade catalogue,
            so there’s no rate history to draw on. Rename the line to a canonical subtrade, or add rates from the SD Rates tab.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <BrioKpiBlock label={`Median /sqm · escalated to ${asOf}`} value={fmt(stats.median)} />
              <div style={{ fontSize: '0.8rem', color: 'var(--brio-text-muted)', lineHeight: 1.6 }}>
                {stats.sampleSize} rate{stats.sampleSize === 1 ? '' : 's'} · {stats.distinctProjects} project{stats.distinctProjects === 1 ? '' : 's'}
                {spread.quartiles && <><br />p25–p75 {fmt(spread.quartiles[0])}–{fmt(spread.quartiles[1])}</>}
                {spread.range && <>{spread.quartiles ? ' · ' : <br />}range {fmt(spread.range[0])}–{fmt(spread.range[1])}</>}
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.74rem', color: 'var(--brio-text-muted)', marginLeft: 'auto' }}>
                Measure basis
                <BrioSelect value={basisMode} onChange={(e) => setBasisMode(e.target.value)}>
                  <option value="all">All bases</option>
                  {Object.entries(MEASURE_BASIS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </BrioSelect>
              </label>
            </div>

            {!costIndex.hasData && (
              <div style={{ fontSize: '0.78rem', color: 'var(--brio-warning, #8a6d3b)', marginBottom: '0.5rem' }}>
                ⚠ No cost-index data loaded — rates shown un-escalated.
              </div>
            )}

            {stats.sampleSize === 0 ? (
              <div style={{ padding: '0.85rem 1rem', background: 'var(--brio-bg, #f7f7f5)', borderRadius: 6, fontSize: '0.85rem' }}>
                No SD rates recorded for this subtrade{basisMode === 'all' ? '' : ` on a ${measureBasisLabel(basisMode).toLowerCase()} basis`} yet.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Project</th>
                    <th style={th}>Basis</th>
                    <th style={{ ...th, textAlign: 'right' }}>Original</th>
                    <th style={{ ...th, textAlign: 'right' }}>Date</th>
                    <th style={{ ...th, textAlign: 'right' }}>Escalated</th>
                    <th style={{ ...th, width: 70 }} />
                  </tr>
                </thead>
                <tbody>
                  {stats.contributors.map((c) => (
                    <tr key={c.id}>
                      <td style={cell}>{c.projectCode || '(manual)'}</td>
                      <td style={cell}>{measureBasisLabel(c.measureBasis)}</td>
                      <td style={numCell}>{fmt(c.rate)}</td>
                      <td style={{ ...numCell, color: 'var(--brio-text-muted)' }}>{c.rateDate || '—'}</td>
                      <td style={numCell}>{fmt(c.escalatedRate)}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => apply(c.escalatedRate)}
                          style={{ fontSize: '0.72rem', padding: '0.12rem 0.45rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, background: 'transparent', cursor: 'pointer' }}
                        >
                          Apply
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </BrioModalBody>
    </BrioModal>
  );
}

export default SdRatePickerModal;
